import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { StickyCallButton } from "./components/StickyCallButton";
import { HomePageV1 } from "./pages/HomePageV1";
import { ServicesPage } from "./pages/ServicesPage";
import { AboutPage } from "./pages/AboutPage";
import { BookingPage } from "./pages/BookingPage";
import { WeddingOfficiantPage } from "./pages/WeddingOfficiantPage";
import { AreasWeServePage } from "./pages/AreasWeServePage";
import { CountyPage } from "./pages/areas/CountyPage";
import { CityPage } from "./pages/areas/CityPage";
import { ApostillePage } from "./pages/services/ApostillePage";
import { EstatePlanningPage } from "./pages/services/EstatePlanningPage";
import { RemoteOnlineNotarizationPage } from "./pages/services/RemoteOnlineNotarizationPage";
import { LoanSigningPage } from "./pages/services/LoanSigningPage";
import { GeneralNotaryPage } from "./pages/services/GeneralNotaryPage";
import { FingerprintingPage } from "./pages/services/FingerprintingPage";
import { I9VerificationPage } from "./pages/services/I9VerificationPage";
import { ProcessServingPage } from "./pages/services/ProcessServingPage";
import { DocumentTranslationPage } from "./pages/services/DocumentTranslationPage";
import { PhotoBoothPage } from "./pages/services/PhotoBoothPage";
import ApostilleOrderPage from "./pages/ApostilleOrderPage";
import TrackOrderPage from "./pages/TrackOrderPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import { AdminOrders, AdminOrderDetail } from "./pages/admin/AdminOrders";
import { AdminBookings, AdminBookingDetail } from "./pages/admin/AdminBookings";
import { AdminNotaries, AdminNotaryDetail } from "./pages/admin/AdminNotaries";
import AdminTasks from "./pages/admin/AdminTasks";
import AdminFinance from "./pages/admin/AdminFinance";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ScheduleSection } from "./components/ScheduleSection";

// Scroll to top on route change so service pages don't open mid-scroll.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

// Public site chrome (header + footer + sticky call). Admin & tracking pages
// render full-bleed without the marketing chrome.
function PublicShell({ children }) {
  return (
    <>
      <Header />
      <div className="flex-1">{children}</div>
      <ScheduleSection />
      <Footer />
      <StickyCallButton />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
            {/* Tracking & admin — no public chrome */}
            <Route path="/track/:token" element={<TrackOrderPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/new" element={<ApostilleOrderPage adminMode />} />
              <Route path="orders/:id" element={<AdminOrderDetail />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="bookings/:id" element={<AdminBookingDetail />} />
              <Route path="notaries" element={<AdminNotaries />} />
              <Route path="notaries/:id" element={<AdminNotaryDetail />} />
              <Route path="tasks" element={<AdminTasks />} />
              <Route path="finance" element={<AdminFinance />} />
            </Route>

            {/* Public site */}
            <Route
              path="*"
              element={
                <PublicShell>
                  <Routes>
                    <Route path="/" element={<HomePageV1 />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/booking" element={<BookingPage />} />

                    <Route path="/areas-we-serve" element={<AreasWeServePage />} />
                    <Route path="/areas/county/:slug" element={<CountyPage />} />
                    <Route path="/areas/city/:slug" element={<CityPage />} />

                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/services/apostille" element={<ApostillePage />} />
                    <Route path="/services/apostille/order" element={<ApostilleOrderPage />} />
                    <Route path="/services/estate-planning" element={<EstatePlanningPage />} />
                    <Route path="/services/remote-online-notarization" element={<RemoteOnlineNotarizationPage />} />
                    <Route path="/services/loan-signing" element={<LoanSigningPage />} />
                    <Route path="/services/general-notary" element={<GeneralNotaryPage />} />
                    <Route path="/services/wedding-officiant" element={<WeddingOfficiantPage />} />
                    <Route path="/services/fingerprinting" element={<FingerprintingPage />} />
                    <Route path="/services/i9-verification" element={<I9VerificationPage />} />
                    <Route path="/services/process-serving" element={<ProcessServingPage />} />
                    <Route path="/services/document-translation" element={<DocumentTranslationPage />} />
                    <Route path="/services/photo-booth" element={<PhotoBoothPage />} />
                  </Routes>
                </PublicShell>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="top-right"
        richColors
        toastOptions={{
          style: {
            fontFamily: "Inter, sans-serif",
          },
        }}
      />
    </div>
  );
}

export default App;
